import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ruleTip } from "@/lib/format";
import { Empty } from "@/components/widgets";
import { QueryBoundary, retryTransient } from "@/lib/queryState";
import { MIN_N, isScoringOnly } from "@/lib/checkpointState";
import { PageHead, Panel, Badge, RuleResult, SegmentedControl, StatCell, fmt, sessionDateLabel } from "@/sentinel";

// Audit order L → E → T → C → B, then the other families the engine emits (S).
const ALL_RULES = ["L1", "L2", "L3", "L4", "E1", "E2", "E3", "T1", "T2", "T3", "C1", "C2", "C3", "B1", "B2", "S1", "S2", "S3"];

function ParameterRegister({ paramReg }) {
  if (!paramReg) return null;
  return (
    <Panel title="Parameter register" icon="SlidersHorizontal" flush data-testid="parameter-register"
      meta={<><Badge icon="Eye">Read-only</Badge><span className="sn-num">version {paramReg.version}</span></>}>
      <div style={{ overflowX: "auto" }}>
        <table className="sn-table">
          <thead>
            <tr><th>P-ID</th><th>Group</th><th>Description</th><th className="r">Value</th><th>Unit / note</th></tr>
          </thead>
          <tbody>
            {(paramReg.rows || []).map((p) => (
              <tr key={p.id} data-testid={`param-${p.id}`}>
                <td className="id">{p.id}</td>
                <td>{p.group}</td>
                <td style={{ color: "var(--text-primary)", whiteSpace: "normal", minWidth: 240 }}>{p.description}</td>
                <td className="num">{typeof p.value === "number" ? p.value : String(p.value)}</td>
                <td className="sn-small sn-faint" style={{ whiteSpace: "normal" }}>{p.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/** One matrix cell: the rule glyph, with the full result for assistive tech and on hover. */
function Cell({ rule, v }) {
  const res = v === "PASS" ? "PASS" : v === "FAIL" ? "FAIL" : v === "UNCONFIRMED" ? "UNCONFIRMED" : "NA";
  return (
    <td title={`${rule} ${v || "NA"}`} style={{ textAlign: "center", ...(res === "FAIL" ? { background: "var(--validation-fail-dim)" } : null) }}>
      <RuleResult result={res}><span className="sn-vh">{rule} {res}</span></RuleResult>
    </td>
  );
}

export default function Thresholds() {
  const { selectedDate } = useAuth();
  const [run, setRun] = useState(null);

  const cpsQ = useQuery({ queryKey: ["checkpoints", selectedDate], queryFn: () => api.getCheckpoints(selectedDate), enabled: !!selectedDate, retry: retryTransient });
  const checkpoints = cpsQ.data || [];
  const runs = checkpoints.filter((c) => !c.recalc_of && !isScoringOnly(c)).map((c) => c.run).sort((a, b) => a - b);
  const activeRun = run || runs[runs.length - 1];

  const candQ = useQuery({ queryKey: ["candidates", selectedDate, activeRun], queryFn: () => api.getCandidates(selectedDate, activeRun), enabled: !!selectedDate && !!activeRun, retry: retryTransient });
  const { data: scores } = useQuery({ queryKey: ["scores", selectedDate], queryFn: () => api.getScores(selectedDate), enabled: !!selectedDate, retry: retryTransient });
  const paramQ = useQuery({ queryKey: ["parameters"], queryFn: () => api.getParameters(), retry: retryTransient });
  const paramReg = paramQ.data;

  const head = (
    <PageHead
      crumb={["Configure", <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span>]}
      title="Old rule settings"
      context={activeRun ? <>Threshold audit v2.3 · rule matrix at R{activeRun}, plus the parameter register the engine reads.</> : "The parameter register the old rule set reads. Kept for reference; the 10:00 am programme does not use these rules."}
      actions={runs.length > 0 && (
        <SegmentedControl label="Run" value={activeRun} onChange={setRun}
          options={runs.map((n) => ({ value: n, label: `R${n}`, testId: `thr-run-${n}` }))} />
      )}
    />
  );

  if (cpsQ.isLoading || cpsQ.isError) return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="thresholds-page">
      {head}
      <QueryBoundary query={cpsQ} loading="page" what="the checkpoints" errorTestId="thresholds-error">{() => null}</QueryBoundary>
    </div>
  );

  if (!runs.length) return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="thresholds-page">
      {head}
      <Empty testId="thresholds-empty" icon="SlidersHorizontal" title="No threshold audit yet">No threshold audit yet. Run the workbook to generate candidate rule matrices.</Empty>
      <ParameterRegister paramReg={paramReg} />
    </div>
  );

  const cands = candQ.data?.candidates || [];
  const diag = scores?.diagnostics;

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="thresholds-page">
      {head}

      {candQ.isError && !candQ.data && <QueryBoundary query={candQ} what={`the candidates for Run ${activeRun}`}>{() => null}</QueryBoundary>}
      <Panel title={`Rule matrix · R${activeRun}`} icon="ClipboardCheck" flush meta={<span>{cands.length} candidates</span>}>
        <div style={{ overflowX: "auto" }}>
          <table className="sn-table" data-testid="threshold-matrix">
            <thead>
              <tr>
                <th className="sticky-col">Candidate</th>
                <th className="r">EV %</th>
                {ALL_RULES.map((r) => <th key={r} title={ruleTip[r]} style={{ textAlign: "center" }}><span className="sn-num">{r}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {cands.map((c) => (
                <tr key={c.candidate_id}>
                  <td className="sticky-col" style={{ color: "var(--text-primary)" }}>
                    <span className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "nowrap" }}>
                      <span className="sn-num">{c.structure}{c.width} {c.short_strike}/{c.long_strike}</span>
                      {c.is_primary && <Badge icon="Crosshair">Primary</Badge>}
                    </span>
                  </td>
                  <td className="num">{c.ev_pct == null ? "—" : fmt(c.ev_pct * 100, "pct", { signed: true })}</td>
                  {ALL_RULES.map((r) => <Cell key={r} rule={r} v={c.rules[r]} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <p className="sn-small sn-faint" style={{ margin: 0 }}>Audit order L → E → T → C → B, then S. Hover a column header for the rule definition.</p>

      {diag && (
        <Panel title="Last-N diagnostics · live sessions only" icon="Sigma" meta={<Badge icon="Sigma">n = {diag.payload.checkpoints_scored ?? "—"}</Badge>}>
          {(diag.payload.checkpoints_scored ?? 0) < MIN_N && <p className="sn-small" style={{ margin: "0 0 var(--space-2)", fontWeight: 600 }}>n &lt; {MIN_N}: illustrative only.</p>}
          <div className="sn-row" style={{ gap: "var(--space-8)" }}>
            <StatCell size="sm" label="Checkpoints scored" value={diag.payload.checkpoints_scored == null ? "—" : String(diag.payload.checkpoints_scored)} />
            <StatCell size="sm" label="Coverage" value={diag.payload.coverage_rate == null ? "—" : `${(diag.payload.coverage_rate * 100).toFixed(0)}%`} sub={`n = ${diag.payload.checkpoints_scored ?? "—"}`} />
            <StatCell size="sm" label="Direction" value={diag.payload.direction_accuracy == null ? "—" : `${(diag.payload.direction_accuracy * 100).toFixed(0)}%`} sub={`n = ${diag.payload.checkpoints_scored ?? "—"}`} />
          </div>
        </Panel>
      )}

      {paramReg && <ParameterRegister paramReg={paramReg} />}
    </div>
  );
}
